import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, mergeProps as _mergeProps, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = { class: "flex items-center gap-2" }
const _hoisted_3 = { class: "text-gray-90 font-medium" }
const _hoisted_4 = {
  class: "overflow-auto",
  style: { height: `calc(100% - 65px)` }
}

import { ref, computed, type MaybeRef, unref } from 'vue'
import { useDraggable } from '@vueuse/core'
import { UiCloseButton } from '@klausapp/ui-kit'

import MoveIcon from './assets/move.svg'

interface Props {
  title?: string
  initialX?: number
  initialY?: number
  containerRef?: MaybeRef<HTMLElement | undefined>
  teleportTo?: string | HTMLElement
  positionStorageKey?: string
  testId?: string
}


export default /*@__PURE__*/_defineComponent({
  ...{
  inheritAttrs: false,
},
  __name: 'UiFloatingModal',
  props: {
    title: { default: '' },
    initialX: { default: 0 },
    initialY: { default: 0 },
    containerRef: { default: undefined },
    teleportTo: { default: undefined },
    positionStorageKey: { default: undefined },
    testId: { default: undefined }
  },
  emits: ["close"],
  setup(__props: any) {

const props = __props





const modal = ref<HTMLElement>()
const header = ref<HTMLElement>()

const containerElement = computed(() => {
  return unref(props.containerRef) || document.documentElement
})

// Load saved position from localStorage
const getSavedPosition = () => {
  if (!props.positionStorageKey) return null
  try {
    const saved = localStorage.getItem(`floating-modal-position-${props.positionStorageKey}`)
    return saved ? JSON.parse(saved) : null
  } catch {
    return null
  }
}

// Validate if a position is within bounds
const isPositionValid = (position: { x: number; y: number }) => {
  const container = containerElement.value
  const containerWidth = container === document.documentElement ? window.innerWidth : container.clientWidth
  const containerHeight = container === document.documentElement ? window.innerHeight : container.clientHeight

  // Check if position is negative or if modal would be mostly off-screen
  // Allow up to 100px off-screen to account for dragging near edges
  const minVisiblePx = 100
  return (
    position.x >= -minVisiblePx &&
    position.y >= -minVisiblePx &&
    position.x < containerWidth &&
    position.y < containerHeight
  )
}

// Calculate centered position (uses estimated modal size)
const getCenteredPosition = () => {
  const container = containerElement.value
  const containerWidth = container === document.documentElement ? window.innerWidth : container.clientWidth
  const containerHeight = container === document.documentElement ? window.innerHeight : container.clientHeight

  // Use estimated modal dimensions (320x700 is typical for evaluation form)
  const estimatedWidth = 320
  const estimatedHeight = 700

  return {
    x: Math.max(0, (containerWidth - estimatedWidth) / 2),
    y: Math.max(0, (containerHeight - estimatedHeight) / 2),
  }
}

// Adjust initial position based on whether we have a container
const adjustedInitialPosition = computed(() => {
  const container = containerElement.value

  // First, try to get saved position from localStorage
  const savedPosition = getSavedPosition()
  if (savedPosition) {
    // Validate if saved position is still valid for current screen size
    if (isPositionValid(savedPosition)) {
      return { x: savedPosition.x, y: savedPosition.y }
    } else {
      // Position is out of bounds, clear it and use centered position
      if (props.positionStorageKey) {
        try {
          localStorage.removeItem(`floating-modal-position-${props.positionStorageKey}`)
        } catch {
          // Ignore storage errors
        }
      }
      return getCenteredPosition()
    }
  }

  // Fall back to provided initial position
  // If using document.documentElement, use coordinates as-is
  if (container === document.documentElement) {
    return { x: props.initialX, y: props.initialY }
  }

  // If using a specific container, coordinates should be relative to that container
  // So we don't need to adjust them - useDraggable will handle the positioning
  return { x: props.initialX, y: props.initialY }
})

const { style: draggableStyle, isDragging } = useDraggable(modal, {
  initialValue: adjustedInitialPosition,
  handle: header,
  containerElement,
  onEnd: (position) => {
    if (props.positionStorageKey) {
      try {
        localStorage.setItem(
          `floating-modal-position-${props.positionStorageKey}`,
          JSON.stringify({ x: position.x, y: position.y })
        )
      } catch {
        // Ignore storage errors (incognito mode, storage full, etc.)
      }
    }
  },
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, {
    to: _ctx.teleportTo,
    disabled: !_ctx.teleportTo
  }, [
    _createElementVNode("div", _mergeProps({
      ref_key: "modal",
      ref: modal,
      class: ['border-gray-20 absolute z-[1000] overflow-hidden rounded-lg border bg-white shadow-md', _ctx.$attrs.class]
    }, { ..._ctx.$attrs, class: undefined }, {
      style: _unref(draggableStyle),
      "data-testid": props.testId
    }), [
      _createElementVNode("div", {
        ref_key: "header",
        ref: header,
        class: _normalizeClass(["border-gray-20 flex select-none items-center justify-between gap-2 border-b bg-white p-5", _unref(isDragging) ? 'cursor-grabbing' : 'cursor-grab'])
      }, [
        _createElementVNode("div", _hoisted_2, [
          _renderSlot(_ctx.$slots, "icon", {}, () => [
            _createVNode(_unref(MoveIcon), { class: "icon-m" })
          ]),
          _renderSlot(_ctx.$slots, "header", {}, () => [
            _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.title), 1 /* TEXT */)
          ])
        ]),
        _createVNode(_unref(UiCloseButton), {
          small: "",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
        })
      ], 2 /* CLASS */),
      _createElementVNode("div", _hoisted_4, [
        _renderSlot(_ctx.$slots, "default")
      ])
    ], 16 /* FULL_PROPS */, _hoisted_1)
  ], 8 /* PROPS */, ["to", "disabled"]))
}
}

})